// React, Next
import { ReactElement } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Icons - the same ones the desktop menu uses, so the two agree
import TroubleshootIcon from '@mui/icons-material/Troubleshoot'
import SensorsIcon from '@mui/icons-material/Sensors'
import AirIcon from '@mui/icons-material/Air'
import HandymanIcon from '@mui/icons-material/Handyman'
import ShieldIcon from '@mui/icons-material/Shield'
import SchoolIcon from '@mui/icons-material/School'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import ApartmentIcon from '@mui/icons-material/Apartment'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'

// Custom
import { PageType, translateHref } from 'assets/functions/links'
import LanguageSwitcher2 from '../tools/buttons/LanguageSwitcher/LanguageSwitcher2'

// Img - the logo files are named after the background they sit on, not the
// ink: `_dark` is the white artwork, which is what this dark header needs.
import KroemtechLogo from 'public/img/logos/kroemtech/kroemtech_logo_dark.svg'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import classes from './MenuSideBar-CSS'

type Props = {
  setDrawerOpen: (state: boolean) => void
}

type Entry = {
  label: string
  href: PageType
  icon: ReactElement
}

/** A set of pages with no page of its own, so its label is not a link. */
type Group = {
  label: string
  icon: ReactElement
  children: Entry[]
}

type Section = {
  /** Small uppercase heading above the group. */
  label: string
  /** The hub page, when the group has one. */
  hub?: Entry
  children: Entry[]
  groups?: Group[]
}

export default function MenuSideBar(props: Props) {
  const { t } = useTranslation('common')

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const close = () => props.setDrawerOpen(false)

  const sections: Section[] = [
    {
      label: t('menu.solutions'),
      hub: {
        label: t('menu.solutions'),
        href: 'solutions',
        icon: <TroubleshootIcon sx={classes.icon} />
      },
      children: [],
      groups: [
        {
          label: t('subMenu.solutionsGroup1'),
          icon: <SensorsIcon sx={classes.icon} />,
          children: [
            {
              label: t('subMenu.solutions4'),
              href: 'iot-air-quality-monitoring',
              icon: <AirIcon sx={classes.icon} />
            }
          ]
        }
      ]
    },
    {
      label: t('menu.services'),
      hub: {
        label: t('menu.services'),
        href: 'services',
        icon: <HandymanIcon sx={classes.icon} />
      },
      children: [
        {
          label: t('subMenu.services6'),
          href: 'ot-cybersecurity',
          icon: <ShieldIcon sx={classes.icon} />
        },
        {
          label: t('subMenu.services7'),
          href: 'ot-awareness-training',
          icon: <SchoolIcon sx={classes.icon} />
        },
        {
          label: t('subMenu.services8'),
          href: 'test-equipment-modernization',
          icon: <BuildCircleIcon sx={classes.icon} />
        }
      ]
    },
    {
      label: t('menu.company'),
      children: [
        {
          label: t('subMenu.company1'),
          href: 'technology',
          icon: <TipsAndUpdatesIcon sx={classes.icon} />
        },
        {
          label: t('subMenu.company3'),
          href: 'about',
          icon: <ApartmentIcon sx={classes.icon} />
        }
      ]
    }
  ]

  const legal: { label: string; href: PageType }[] = [
    { label: t('footer.articles'), href: 'articles' },
    { label: t('footer.privacy'), href: 'privacy' },
    { label: t('footer.legalNotice'), href: 'legal-notice' },
    { label: t('footer.termsOfUse'), href: 'terms-of-use' }
  ]

  const getRow = (entry: Entry, level: 'hub' | 'child' | 'groupChild') => (
    <Box
      key={`${entry.href}-${level}`}
      component={Link}
      href={translateHref(currentLocale, entry.href)}
      onClick={close}
      sx={
        {
          hub: classes.sectionLink,
          child: classes.childLink,
          groupChild: classes.groupChildLink
        }[level]
      }
    >
      {entry.icon}
      {entry.label}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Box sx={classes.mainBox} role="presentation">
      <Box sx={classes.header}>
        <Link href={`/${currentLocale}/`} onClick={close}>
          <Image src={KroemtechLogo} alt="KROEMTECH" height={22} />
        </Link>
        <CloseIcon onClick={close} sx={classes.closeIcon} />
      </Box>

      <Box component="nav" sx={classes.nav}>
        {sections.map((section, idx) => (
          <Box key={section.label}>
            {idx > 0 && <Box sx={classes.divider} />}

            <Typography component="span" sx={classes.sectionLabel}>
              {section.label}
            </Typography>

            {section.hub && getRow(section.hub, 'hub')}
            {section.children.map(entry =>
              getRow(entry, section.hub ? 'child' : 'hub')
            )}

            {section.groups?.map(group => (
              <Box key={group.label}>
                <Typography component="span" sx={classes.groupLabel}>
                  {group.icon}
                  {group.label}
                </Typography>

                {group.children.map(entry => getRow(entry, 'groupChild'))}
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Box sx={classes.ctaBox}>
        <Link
          href={translateHref(currentLocale, 'contact')}
          onClick={close}
          style={{ textDecoration: 'none' }}
        >
          <Button
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardIosIcon sx={{ transform: 'scale(0.7)' }} />}
            sx={{
              background: themeColors.success.main,
              borderRadius: '4px',
              padding: '0.7rem 1rem',
              fontWeight: 600,
              letterSpacing: '0.03em',
              boxShadow: 'none',
              '&:hover': {
                background: themeColors.success.darker,
                boxShadow: 'none'
              }
            }}
          >
            {t('subMenu.company2')}
          </Button>
        </Link>
      </Box>

      <Box sx={classes.legalBox}>
        {legal.map(item => (
          <Box
            key={item.href}
            component={Link}
            href={translateHref(currentLocale, item.href)}
            onClick={close}
            sx={classes.legalLink}
          >
            {item.label}
          </Box>
        ))}
      </Box>

      <Box sx={classes.footerBox}>
        <LanguageSwitcher2 currentLocale={currentLocale} />
      </Box>
    </Box>
  )
}
